import { CharacterForm } from '../types';
import { CensorshipType } from './geminiCharacterAnalysis';

// ============================================
// STYLE KEYWORD SANITIZATION (FINAL SAFETY NET)
// ============================================

/**
 * Banned style keywords - these must NEVER appear in the final prompt.
 * The hardcoded layer adds the correct style; user/Gemini input must be clean.
 */
const BANNED_STYLE_TERMS = [
  // Anime/Manga
  'anime', 'anime style', 'manga', 'manga style', 'cel shaded', 'cel-shaded',
  'japanese animation', 'studio ghibli', 'ghibli', 'mappa', 'ufotable',
  // Photorealistic
  'photorealistic', 'photorealism', 'realistic', 'hyperrealistic',
  'photo', 'photograph', 'photography', 'raw photo',
  // 3D/Game
  '3d render', '3d model', 'cgi', 'unreal engine', 'unity', 'video game',
  'game character', 'pbr textures', 'rendered', 'blender',
  // Comic
  'comic book', 'comic style', 'graphic novel', 'western comic',
  // Cartoon
  'cartoon', 'cartoonish', 'toon', 'pixar', 'disney', 'dreamworks',
  // Art styles
  'oil painting', 'watercolor', 'watercolour', 'sketch', 'pencil drawing',
  'digital art', 'digital painting', 'illustration', 'illustrated', 'concept art',
  // Technical
  '8k', '4k', 'high quality', 'ultra detailed', 'highly detailed',
  'masterpiece', 'best quality',
  // Cinematic
  'cinematic', 'film', 'movie', 'movie still',
  // Artist references
  'artstation', 'deviantart', 'trending on',
];

/**
 * Strip all style keywords from user input.
 * This is the FINAL safety net before the prompt goes to the image model.
 */
function sanitizeStyleFromInput(input: string): string {
  let cleaned = input;

  // Sort by length (longest first) to avoid partial replacements
  const sorted = [...BANNED_STYLE_TERMS].sort((a, b) => b.length - a.length);

  for (const term of sorted) {
    const regex = new RegExp(`\\b${term}\\b[,.]?\\s*`, 'gi');
    cleaned = cleaned.replace(regex, '');
  }

  // Clean up artifacts
  return cleaned
    .replace(/,\s*,/g, ',')
    .replace(/\s+,/g, ',')
    .replace(/,\s+/g, ', ')
    .replace(/\s+/g, ' ')
    .replace(/^[,\s]+/, '')
    .replace(/[,\s]+$/, '')
    .trim();
}

/**
 * Layer-Specific Prompt Engineering
 * Enforces strict constraints per layer to prevent generation mistakes
 *
 * NEW WORKFLOW (with head transposition):
 * Body: Generated WITH normal head (will be replaced via transposition)
 * Head: Detailed head generated separately, then composited onto body
 * Clothing: Full body with clothing
 * Accessories: Items only, transparent background
 *
 * HUMANOID:
 * Body: WITH complete human head (basic features), nude (no clothing)
 * Head: Detailed head (hair, facial features), clothless, headshot only
 * Clothing: Full body with clothing
 * Accessories: Items only, transparent background
 *
 * CREATURE:
 * Body: WITH complete creature head (species-appropriate features), natural body coverage
 * Head: Detailed creature head (species-specific features), clothless, headshot only
 * Clothing: Full body with clothing (if applicable)
 * Accessories: Items only, transparent background
 */

export interface LayerPromptConfig {
  basePrompt: string;
  constraints: string[];
  negativePrompts: string[];
  styleSuffix: string;
}

export function isPhotographicStyle(style: string): boolean {
  return /photorealis|photograph|\brealistic\b|\braw photo\b/i.test(style);
}

/**
 * SFW base layer descriptions by gender
 * Low-rise athletic compression shorts in black - model-safe terminology
 * IMPORTANT: These MUST be visible in the generated image
 * Exported so reference sheet generation can use them
 */
export const SFW_UNDERWEAR = {
  male: 'wearing black low-rise athletic compression shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
  female: 'wearing black soft fabric triangle bikini top(s) with string/spaghetti straps covering all bust areas and black high-cut side low-rise string bikini bottoms covering lower body',
  neutral: 'wearing black low-rise athletic compression shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
};

// Conservative fallback underwear for retry after content moderation failures
export const SFW_UNDERWEAR_CONSERVATIVE = {
  male: 'wearing black low-rise athletic compression shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
  female: 'wearing black string spandex sports bra covering chest area and black low-rise yoga shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
  neutral: 'wearing black low-rise athletic compression shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
};

// Most conservative fallback - tube top and high-cut bottoms (last resort retry)
export const SFW_UNDERWEAR_TUBETOP = {
  male: 'wearing black low-rise athletic compression shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
  female: 'wearing black tube top covering chest and black high-cut low-rise bottoms covering lower body',
  neutral: 'wearing black low-rise athletic compression shorts covering lower body (form-fitting, sits low on hips, mid-thigh length)',
};

/**
 * Get underwear/coverage prompt from CensorshipType
 * Maps the Gemini-detected censorship type to the appropriate coverage prompt
 */
export function getUnderwearFromCensorshipType(censorshipType: CensorshipType): string {
  switch (censorshipType) {
    case 'female_censored':
    case 'creature_female_censored':
      // Female anatomy (humanoid or creature) - sports bra + shorts
      return SFW_UNDERWEAR.female;
    case 'male_censored':
    case 'creature_male_censored':
      // Male anatomy (humanoid or creature) - shorts only
      return SFW_UNDERWEAR.male;
    case 'creature_uncensored':
      // No visible sex organs - no coverage needed
      return '';
    default:
      return SFW_UNDERWEAR.neutral;
  }
}

/**
 * Check if censorship type requires any body coverage
 */
export function requiresCoverage(censorshipType: CensorshipType): boolean {
  return censorshipType !== 'creature_uncensored';
}

/**
 * Get the form (humanoid/creature) from censorship type
 */
export function getFormFromCensorshipType(censorshipType: CensorshipType): CharacterForm {
  return censorshipType.startsWith('creature') ? 'creature' : 'humanoid';
}

/**
 * Body Layer: Full body character
 * When editing (inputImagePresent=true): Allow changes to any part including face/head/hair
 * When generating new: Include default head with basic features
 * Constraints:
 * - Always wears black underwear (unless NSFW mode)
 * - Humanoid: Body with natural skin
 * - Creature: Natural body coverage (fur/scales/feathers/skin)
 * - MUST show full body from head to toe
 */
export function buildBodyLayerPrompt(
  userDescription: string,
  styleSuffix: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false,
  gender?: 'male' | 'female' | 'neutral',
  nsfwMode: boolean = false,
  retryLevel: number = 0  // 0=normal bikini, 1=no "bare" words, 2=sports bra, 3=tube top
): LayerPromptConfig {
  const isCreature = form === 'creature';

  // Fitted athletic base layers leave the body usable for later outfit fitting.
  // The default sports set was verified with Sunburst High and a face reference.
  if (!isCreature && !nsfwMode && isPhotographicStyle(styleSuffix)) {
    const shorts = 'matching fitted black short-inseam compression shorts ending at the upper thigh';
    const outfit = gender === 'male'
      ? 'opaque matte black fitted short-inseam compression shorts ending at the upper thigh'
      : retryLevel > 0
        ? `an opaque matte black high-neck sports bra ending at the ribcage and ${shorts}`
        : `an opaque matte black racerback sports bra and ${shorts}`;
    return {
      basePrompt: `Full-body fashion fitting reference photograph of ${userDescription}. Wearing ${outfit}. This simple fitted base layer is worn underneath later outfits. Plain matte opaque fabric with complete intimate-area coverage. Keep the abdomen, arms and legs visible for fitting. Neutral standing pose facing the camera, natural proportions, complete head and feet visible with comfortable margins, plain light grey background (#D4D4D4), even studio lighting. ${styleSuffix}`,
      constraints: [
        `BASE LAYER - ${outfit}; preserve the visible waist, arms and legs for later outfit fitting`,
        'NATURAL ANATOMY - preserve head-to-body scale, torso length and limb proportions',
        'CAMERA DISTANCE - fit the complete figure with comfortable margins; never compress or stretch the body',
        'FRONT FACING - neutral standing pose, arms relaxed at sides',
        'REFERENCE IDENTITY - preserve the reference portrait face, hairstyle and skin tone',
        'BACKGROUND - plain light grey with even studio lighting',
      ],
      negativePrompts: ['cropped head or feet, distorted proportions, artificial skin, busy background'],
      styleSuffix,
    };
  }

  // Build gender-specific body description for stronger enforcement (humanoids only)
  let genderBodyDesc = '';
  if (!isCreature) {
    if (gender === 'male') {
      genderBodyDesc = 'masculine male body, male physique, male anatomy, ';
    } else if (gender === 'female') {
      genderBodyDesc = 'feminine female body, female physique, female anatomy, ';
    }
  }

  // Use underwear unless NSFW mode is enabled
  // Retry levels: 0=normal bikini, 1=normal bikini (no bare words), 2=sports bra, 3=tube top
  const effectiveGender = gender || 'neutral';
  let underwearSource = SFW_UNDERWEAR;
  if (retryLevel >= 3) {
    underwearSource = SFW_UNDERWEAR_TUBETOP;
  } else if (retryLevel >= 2) {
    underwearSource = SFW_UNDERWEAR_CONSERVATIVE;
  }
  const underwearPrompt = nsfwMode ? '' : underwearSource[effectiveGender];

  // On retry level 1+, avoid "bare" words that might trigger content filters
  const skipBareWords = retryLevel >= 1;

  // When editing, don't enforce specific head constraints - user can change anything
  // When generating new, include basic head prompt
  let basePrompt: string;

  // Fit the camera around the figure rather than forcing anatomy to fit the canvas.
  const framingPrompt = 'complete figure visible with a small comfortable margin above the head and below the feet, preserve intended anatomical proportions, adjust camera distance to fit the figure rather than compressing or stretching the body';

  if (inputImagePresent) {
    // EDITING MODE: Allow full control, no head restrictions
    basePrompt = isCreature
      ? `${userDescription}, natural body form, ${framingPrompt}, front facing standing pose, facing camera, minimal design, flat light grey background, solid color, no gradient, ${styleSuffix}`
      : nsfwMode
        ? `${genderBodyDesc}${userDescription}, natural human body with realistic skin and muscle definition, ${framingPrompt}, front facing standing pose, facing camera, nude body, character design, flat light grey background, solid color, no gradient, ${styleSuffix}`
        : `${genderBodyDesc}${userDescription}, natural human body with realistic skin and muscle definition, ${framingPrompt}, front facing standing pose, facing camera, ${underwearPrompt}, character design, flat light grey background, solid color, no gradient, ${styleSuffix}`;
    basePrompt += ", apply the requested changes to the reference image";
  } else {
    // NEW GENERATION: Include default head
    const headPrompt = isCreature
      ? "complete creature head matching the body type, with basic facial features appropriate to the species, natural expression, facing forward"
      : "complete human head with natural facial features matching the character description, facing forward";

    basePrompt = isCreature
      ? `${userDescription}, natural body form, ${framingPrompt}, front facing standing pose, facing camera, ${headPrompt}, minimal design, flat light grey background, solid color, no gradient, ${styleSuffix}`
      : nsfwMode
        ? `${genderBodyDesc}${userDescription}, natural human body with realistic skin and muscle definition, ${framingPrompt}, front facing standing pose, facing camera, ${headPrompt}, nude body, character design, flat light grey background, solid color, no gradient, ${styleSuffix}`
        : `${genderBodyDesc}${userDescription}, natural human body with realistic skin and muscle definition, ${framingPrompt}, front facing standing pose, facing camera, ${headPrompt}, ${underwearPrompt}, character design, flat light grey background, solid color, no gradient, ${styleSuffix}`;
  }

  // Constraints - only enforce head-specific rules for new generation
  const constraints: string[] = [];

  if (!inputImagePresent) {
    // NEW GENERATION: Include head constraints
    constraints.push("COMPLETE HEAD - full head with basic facial features visible");
    constraints.push(isCreature ? "CREATURE FEATURES - species-appropriate head features (snout, muzzle, ears, etc.)" : "HUMAN FEATURES - natural human facial features, proportional to body");
  }

  // Common constraints for both modes
  constraints.push(
    "BARE HEAD - NO sunglasses, NO hats, NO helmets, NO head accessories, NO eyewear - only natural head and face",
    "NATURAL EXPRESSION - neutral, relaxed expression",
    "NATURAL SKIN TEXTURE - realistic skin texture, not plastic or shiny",
  );

  // Only enforce underwear for humanoids, not creatures
  if (!nsfwMode && !isCreature) {
    constraints.push(`BLACK UNDERWEAR - ${underwearSource[effectiveGender]}`);
  }

  constraints.push(
    "FRONT FACING - direct frontal view, body and face facing camera, not turned or angled",
    isCreature
      ? "NATURAL BODY COVERAGE - appropriate body surface (fur, scales, feathers, skin, exoskeleton, or other natural covering)"
      : skipBareWords
        ? "NATURAL SKIN - human body with natural skin texture (body hair acceptable)"
        : "BARE SKIN - natural bare human body (body hair acceptable)",
    "FULL BODY - complete figure from head to toes with comfortable space above the head and below the feet",
    isCreature ? "ANATOMY FIRST - preserve species-appropriate proportions and limb lengths" : "ANATOMY FIRST - preserve natural head-to-body scale, torso length and limb proportions appropriate to the character and chosen style",
    "CAMERA DISTANCE - fit the frame around the figure; never squash, stretch or shorten the body to fill the canvas",
    "CENTERED - character perfectly centered in frame",
    "STANDING POSE - standing upright on feet (any standing variation allowed)",
    "FLAT GREY BACKGROUND - solid light grey color (hex #D4D4D4), absolutely no gradient, no shading",
  );

  if (inputImagePresent) {
    constraints.push("APPLY REQUESTED CHANGES - make the edits described in the prompt");
  }

  const negativePrompts = [
    // FRONT FACING - prevent angled/turned poses
    "profile view, side view, 3/4 view, turned body, angled body, looking away, back view, rear view",
    "hollow neck, empty shell, hole in neck, gore, blood, wound, open neck",
    "plastic skin, artificial, joints, robot, android, dummy, rigid",
    // HEAD ACCESSORIES - explicitly prevent any head accessories
    "sunglasses, glasses, eyewear, goggles, shades, spectacles",
    "hat, helmet, cap, headgear, headband, bandana, crown, tiara, headpiece",
    "head accessories, hair accessories, face accessories",
    // For humanoids: clothing constraints (with underwear exception unless NSFW)
    // For creatures: no clothing constraints at all (creatures don't wear underwear)
    ...(isCreature
      ? ["clothing, shirt, pants, dress, jacket, coat, visible fabric"]
      : nsfwMode
        ? ["clothing, shirt, pants, dress, jacket, coat, visible fabric"]
        : ["clothing, shirt, pants, dress, jacket, coat, visible fabric except black underwear"]),
    "fashion accessories, jewelry, belts, scarves, handbags",
    "complex background, busy scene, props, colors, textures in background, gradient background",
    // Only enforce nudity constraints for humanoids, not creatures
    ...(nsfwMode || isCreature ? [] : ["nude, naked, no underwear, exposed genitals, exposed breasts"]),
    "partial body, cropped, cut off",
    "tiny distant subject, squashed anatomy, compressed torso, shortened limbs, stretched body",
    "colored background, white background, black background",
  ];

  return {
    basePrompt,
    constraints,
    negativePrompts,
    styleSuffix,
  };
}

/**
 * Face Layer: Close-up portrait, bald, no clothes, headshot
 * Constraints:
 * - MUST be bald/no hair visible
 * - MUST be clothless (head and shoulders only)
 * - MUST be close-up portrait/headshot only
 * - MUST focus on facial features
 */
export function buildFaceLayerPrompt(
  userDescription: string,
  styleSuffix: string,
  bodyReference?: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false
): LayerPromptConfig {
  const baseDesc = bodyReference
    ? `${userDescription}, facial features that complement ${bodyReference}`
    : userDescription;

  let basePrompt = `Close-up portrait headshot of face, ${baseDesc}, completely bald head (absolutely no hair visible), head and shoulders only, no clothes on shoulders/neck, bare shoulders, professional character design, flat light grey background, solid color, no gradient, soft studio lighting, 4:5 headshot aspect, ${styleSuffix}`;

  if (inputImagePresent) {
    basePrompt += ", follow the structure and painted modifications of the reference image exactly";
  }

  const constraints = [
    "BALD HEAD - absolutely no hair on head, scalp only",
    "HEADSHOT ONLY - close-up portrait, from chin to top of head",
    "FILL FRAME - face and shoulders occupy entire image area",
    "CENTERED - perfectly centered face",
    "FACIAL FOCUS - detailed facial features, expression, skin texture",
    "NO CLOTHING - shoulders bare, no neckline or collar visible",
    "NO HAIR ANYWHERE - face must be completely hairless",
    "FLAT GREY BACKGROUND - solid light grey color (hex #D4D4D4), absolutely no gradient, no shading",
    "SOFT LIGHTING - diffused, flattering headshot lighting",
    "SHOULDERS VISIBLE - neck and shoulder area bare",
  ];

  if (inputImagePresent) {
    constraints.push("FOLLOW INPUT IMAGE - respect the structure and painted modifications of the reference image");
  }

  return {
    basePrompt,
    constraints,
    negativePrompts: [
      "hair, hairstyle, bangs, braids, highlights, any head hair",
      "clothing, shirt, jacket, collar, neckline, fabric on neck/shoulders",
      "full body, torso below shoulders",
      "wide shot, distant view, group shot, small face",
      "accessories, glasses, hats, headwear",
      "body art, tattoos on face",
      "partial face, cropped face",
      "colored background, white background, black background, studio backdrop",
    ],
    styleSuffix,
  };
}

/**
 * Hair Layer: Hairstyle on head, clothless, headshot focus
 * For Creatures: This might be skipped externally, but if called, it treats it as head features.
 */
export function buildHairLayerPrompt(
  userDescription: string,
  styleSuffix: string,
  faceReference?: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false
): LayerPromptConfig {
  const baseDesc = faceReference
    ? `${userDescription}, hairstyle that complements ${faceReference}`
    : userDescription;

  let basePrompt = `Character hairstyle front view, ${baseDesc}, head and shoulders only, no clothes visible on neck/shoulders, bare shoulders, professional character design, flat light grey background, solid color, no gradient, studio lighting, 4:5 headshot aspect, ${styleSuffix}`;

  if (inputImagePresent) {
    // For img2img: ADD HAIR to the bald reference while preserving face
    // Be EXTREMELY explicit about the hair requirement and face preservation
    basePrompt = `CRITICAL: Add a FULL, COMPLETE ${baseDesc} hairstyle to this bald person.
The input shows a BALD HEAD - you MUST generate THICK, FULL HAIR covering the entire scalp.
HAIR REQUIREMENTS: Long flowing strands, full volume, covers the entire top of head, sides of head, and back of head.
DO NOT just add wisps or thin hair - add a COMPLETE, THICK, VOLUMINOUS hairstyle.
FACE PRESERVATION: Keep the EXACT same face - same eyes, same nose, same lips, same skin tone, same expression. Only the hair should change.
Style: ${baseDesc}. ${basePrompt}`;
  }

  const constraints = [
    "ADD HAIR - must add visible hairstyle to the bald head",
    "HAIR VISIBLE - must show hairstyle clearly on head",
    "HEADSHOT FRONT VIEW - front-facing portrait showing hair",
    "FILL FRAME - hair/head occupies full image area",
    "CENTERED - centered composition",
    "NO CLOTHES - shoulders bare, no clothing visible",
    "HEAD AND SHOULDERS - from chin to top of head with neck/shoulders",
    "BARE NECK/SHOULDERS - no fabric, collar, or clothing",
    "FLAT GREY BACKGROUND - solid light grey color (hex #D4D4D4), absolutely no gradient, no shading",
    "HAIRSTYLE FOCUS - hair should be the main visual focus",
    "PRESERVE FACE - keep the same face from reference, only add hair",
  ];

  if (inputImagePresent) {
    constraints.push("ADD HAIR TO BALD HEAD - the reference is bald, you MUST add hair");
    constraints.push("PRESERVE FACE IDENTITY - keep exact same facial features from reference");
  }

  return {
    basePrompt,
    constraints,
    negativePrompts: [
      "clothing, shirt, jacket, dress, sweater, collar",
      "full body, torso, arms at sides",
      "accessories on body, jewelry, belts",
      "profile view, side angle, 3/4 view",
      "wide shot, distant view, small head",
      "hats, scarves, headwear covering hair",
      "cropped hair, partial hairstyle",
      "colored background, white background, black background",
    ],
    styleSuffix,
  };
}

/**
 * Head Layer: Combined face and hair generation
 * This generates a complete head portrait with both facial features and hairstyle
 * Constraints:
 * - MUST be close-up portrait/headshot
 * - MUST include both face AND hair
 * - MUST be clothless (head and shoulders only)
 */
export function buildHeadLayerPrompt(
  userDescription: string,
  styleSuffix: string,
  bodyReference?: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false,
  gender?: 'male' | 'female' | 'neutral'
): LayerPromptConfig {
  const baseDesc = bodyReference
    ? `${userDescription}, head that complements ${bodyReference}`
    : userDescription;

  // Build gender-specific prompt prefix for stronger enforcement
  let genderPrefix = '';
  if (gender === 'male') {
    genderPrefix = 'masculine male face, handsome man, male facial structure, masculine features, ';
  } else if (gender === 'female') {
    genderPrefix = 'feminine female face, beautiful woman, female facial structure, feminine features, ';
  }

  // CINEMATOGRAPHY FRAMING: Big Close-Up (BCU), Rule of Thirds, tight headroom
  // FRONT FACING is critical for character design consistency
  // Composition: head ~70% vertical height, eyes at upper 1/3 line, minimal headroom
  let basePrompt = `BIG CLOSE-UP HEADSHOT (BCU), tight shot portrait, Front facing close-up headshot portrait with face and hair, frontal view looking at camera, ${genderPrefix}${baseDesc}, complete hairstyle visible, detailed facial features, CINEMATOGRAPHY COMPOSITION: head occupies 70 percent of vertical frame height, eyes positioned at upper third horizontal line following rule of thirds, centrally composed face, minimal headroom with top of hair just touching top edge of frame, bare shoulders, shoulders WIDER than frame extending beyond left and right edges, upper chest and shoulders bleed off the sides of the image naturally, no hard horizontal crop at shoulders, face is the focal point, professional character design, flat light grey background (hex #D4D4D4), solid color, no gradient, soft diffused portrait lighting with subtle catchlights in eyes, 3:4 portrait aspect, ${styleSuffix}`;

  if (inputImagePresent) {
    basePrompt += ", follow the structure and painted modifications of the reference image exactly while maintaining portrait framing";
  }

  const constraints = [
    "BIG CLOSE-UP (BCU) - tight headshot framing, face dominates frame",
    "FRONT FACING - direct frontal view, looking straight at camera, not angled or turned",
    "HEAD SIZE - head occupies approximately 70% of vertical frame height",
    "RULE OF THIRDS - eyes positioned at upper third horizontal line",
    "CENTRAL COMPOSITION - face centered horizontally in frame",
    "MINIMAL HEADROOM - top of hair just touching or nearly touching top edge, small gap only",
    "BARE SHOULDERS - shoulders must be bare with no clothing, skin visible on neck and shoulders",
    "SHOULDERS EXTEND BEYOND FRAME - shoulders must be WIDER than the image, bleeding off left and right edges naturally",
    "NO HARD CROP AT SHOULDERS - never cut off horizontally at shoulder level, shoulders trail off sides",
    "FACE AND HAIR - must show both detailed facial features AND complete hairstyle",
    "FACIAL FOCUS - detailed facial features, expression, skin texture",
    "HAIR VISIBLE - complete hairstyle clearly visible",
    "FLAT GREY BACKGROUND - solid light grey color (hex #D4D4D4), absolutely no gradient, no shading, consistent color",
    "PORTRAIT LIGHTING - soft diffused key light with fill, subtle catchlights in eyes",
  ];

  if (inputImagePresent) {
    constraints.push("FOLLOW INPUT IMAGE - respect the structure and painted modifications of the reference image");
  }

  return {
    basePrompt,
    constraints,
    negativePrompts: [
      // Angle - must be front facing
      "profile view, side view, 3/4 view, turned head, angled face, looking away",
      // Clothing - no fabric visible
      "clothing, shirt, jacket, collar, neckline, fabric on neck or shoulders",
      // Bad framing - head must be large and fill frame
      "full body shot, legs visible, waist visible, torso fully visible",
      "wide shot, distant view, group shot, small face, face too small",
      "excessive headroom, too much space above head, head too low in frame",
      "head too small, face occupies less than half the frame",
      "medium shot, mid shot, cowboy shot",
      "awkward crop at neck, cut off at neck, floating head",
      "cropped at shoulders, horizontal crop at shoulders, shoulders fully visible within frame",
      "shoulders contained within image, both shoulders visible edge to edge",
      "hard horizontal cutoff, abrupt shoulder crop",
      // Face issues
      "body art, tattoos on face",
      "partial face, cropped face",
      "bald, no hair, hairless head",
      // Background consistency
      "colored background, white background, black background, studio backdrop, gradient background",
      "background variation, background pattern, textured background",
      "cropped hair, hair cut off at frame edge",
    ],
    styleSuffix,
  };
}

/**
 * Clothing Layer: Full body with clothes
 * Constraints:
 * - MUST show clothing/outfit
 * - MUST be full body
 * - MUST be professional character design
 */
export function buildClothingLayerPrompt(
  userDescription: string,
  styleSuffix: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false
): LayerPromptConfig {
  let basePrompt = `Full body character wearing ${userDescription}, complete outfit visible, professional character design, flat light grey background, solid color, no gradient, studio lighting, ${styleSuffix}`;

  if (inputImagePresent) {
    basePrompt += ", follow the structure and painted modifications of the reference image exactly";
  }

  const constraints = [
    "FULL BODY - complete figure with comfortable margins above the head and below the feet",
    "PRESERVE ANATOMY - maintain the character's head-to-body scale, torso length and limb proportions; adjust camera distance rather than compressing the figure",
    "CENTERED - perfectly centered",
    "CLOTHING VISIBLE - outfit clearly shown",
    "PROFESSIONAL POSE - standing or neutral pose",
    "FLAT GREY BACKGROUND - solid light grey color (hex #D4D4D4), absolutely no gradient, no shading",
    "STUDIO SETTING - clean background",
  ];

  if (inputImagePresent) {
    constraints.push("FOLLOW INPUT IMAGE - respect the structure and painted modifications of the reference image");
  }

  return {
    basePrompt,
    constraints,
    negativePrompts: [
      "partial body, cropped, cut off",
      "small character, distant view, wide shot",
      "busy background, complex scene, props",
      "extreme angles, unusual perspective",
      "colored background, white background, black background",
    ],
    styleSuffix,
  };
}

/**
 * Accessories Layer: Items only, transparent background
 * Constraints:
 * - MUST show items/accessories only
 * - MUST NOT show body or clothing
 * - MUST have transparent/clean background
 */
export function buildAccessoriesLayerPrompt(
  userDescription: string,
  styleSuffix: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false
): LayerPromptConfig {
  let basePrompt = `${userDescription}, items and accessories only, no body, no clothing, no person, isolated items, grey empty background, professional product style, clear lighting, ${styleSuffix}`;

  if (inputImagePresent) {
    basePrompt += ", follow the structure and painted modifications of the reference image exactly";
  }

  const constraints = [
    "ITEMS ONLY - accessories, weapons, jewelry, props",
    "NO BODY - absolutely no character body, hands, or person",
    "NO CLOTHING - items rendered separately",
    "GREY BACKGROUND - neutral grey empty background, clean and simple",
    "PROFESSIONAL STYLE - product photography style",
    "CLEAR VISIBILITY - items well-lit and visible",
  ];

  if (inputImagePresent) {
    constraints.push("FOLLOW INPUT IMAGE - respect the structure and painted modifications of the reference image");
  }

  return {
    basePrompt,
    constraints,
    negativePrompts: [
      "person, body, torso, hands, arms, legs, character",
      "clothing, worn items, fabric",
      "busy background, scene, environment, props",
      "partial items, cropped, cut off",
      "blurry, low quality",
      "colored background, white background, black background",
    ],
    styleSuffix,
  };
}

/**
 * Get human-readable constraints for display in UI
 * Used by DualPromptReview to show what will be enforced
 *
 * Returns simplified, user-friendly constraints (technical details hidden)
 * Supports both legacy CharacterForm and new CensorshipType
 */
export function getLayerConstraintsForDisplay(
  layerType: 'body' | 'head',
  formOrCensorshipType: CharacterForm | CensorshipType = 'humanoid'
): string[] {
  if (layerType === 'body') {
    return [
      'Full body',
      'Grey background',
      'Soft studio lighting',
      'No clothing or accessories',
    ];
  } else {
    return [
      'Headshot',
      'Grey background',
      'Soft studio lighting',
      'No clothing or accessories',
    ];
  }
}

/**
 * Build complete generation request with enforced constraints
 * This wraps the prompt with hard constraints for the model
 */
export function buildEnforcedGenerationPrompt(
  layerType: 'body' | 'head' | 'face' | 'hair' | 'clothing' | 'accessories',
  userInput: string,
  styleSuffix: string,
  referenceDescription?: string,
  form: CharacterForm = 'humanoid',
  inputImagePresent: boolean = false,
  gender?: 'male' | 'female' | 'neutral',
  nsfwMode: boolean = false,
  retryLevel: number = 0  // 0=normal bikini, 1=no "bare" words, 2=sports bra, 3=tube top
): string {
  // ⚠️ FINAL SAFETY NET: Strip any style keywords from user input
  // Style is ONLY controlled by styleSuffix (from hardcoded archetype)
  const sanitizedUserInput = sanitizeStyleFromInput(userInput);
  const sanitizedReference = referenceDescription ? sanitizeStyleFromInput(referenceDescription) : undefined;

  // Log if we stripped anything
  if (sanitizedUserInput !== userInput) {
    console.log('🧹 Stripped style keywords from userInput:', {
      before: userInput.substring(0, 100),
      after: sanitizedUserInput.substring(0, 100)
    });
  }

  let config: LayerPromptConfig = {
    basePrompt: sanitizedUserInput + ', ' + styleSuffix,
    constraints: [],
    negativePrompts: [],
    styleSuffix,
  };

  switch (layerType) {
    case 'body':
      config = buildBodyLayerPrompt(sanitizedUserInput, styleSuffix, form, inputImagePresent, gender, nsfwMode, retryLevel);
      break;
    case 'head':
      config = buildHeadLayerPrompt(sanitizedUserInput, styleSuffix, sanitizedReference, form, inputImagePresent, gender);
      break;
    case 'face':
      // Legacy support - redirect to head layer
      config = buildHeadLayerPrompt(sanitizedUserInput, styleSuffix, sanitizedReference, form, inputImagePresent, gender);
      break;
    case 'hair':
      // Legacy support - redirect to head layer
      config = buildHeadLayerPrompt(sanitizedUserInput, styleSuffix, sanitizedReference, form, inputImagePresent, gender);
      break;
    case 'clothing':
      config = buildClothingLayerPrompt(sanitizedUserInput, styleSuffix, form, inputImagePresent);
      break;
    case 'accessories':
      config = buildAccessoriesLayerPrompt(sanitizedUserInput, styleSuffix, form, inputImagePresent);
      break;
  }

  // === BUILD FINAL PROMPT ===
  // Style is now handled entirely by archetype prompts (characterArchetypes.ts)
  // and style-specific keywords (characterStyles.ts) - no legacy stylePrefix needed

  let finalPrompt: string;

  // Edit mode prefix (if editing - this goes FIRST for maximum attention)
  let editPrefix = '';
  if (inputImagePresent) {
    if (layerType === 'head') {
      editPrefix = 'EDITING EXTRA CLOSEUP HEADSHOT IMAGE - Apply the following changes while keeping extra closeup portrait framing (face focal point, bare shoulders extending beyond frame edges, shoulders bleed off sides naturally, no clothing), GREY BACKGROUND only: ';
    } else if (layerType === 'body') {
      editPrefix = 'EDITING BODY IMAGE - Apply the following changes while maintaining full body visibility and grey background: ';
    } else {
      editPrefix = 'EDITING IMAGE - Apply the following changes while preserving other areas: ';
    }
  }

  // Constraints suffix (layer-specific rules)
  const constraintsSuffix = config.constraints.length > 0
    ? ` Requirements: ${config.constraints.join('. ')}.`
    : '';

  // FINAL PROMPT ORDER: [EDIT PREFIX] [USER DESCRIPTION + ARCHETYPE + STYLE KEYWORDS] [CONSTRAINTS]
  finalPrompt = editPrefix + config.basePrompt + constraintsSuffix;

  console.log('📝 Final Prompt Structure:', {
    editMode: inputImagePresent ? 'YES' : 'No',
    constraints: config.constraints.length,
    promptLength: finalPrompt.length
  });

  return finalPrompt;
}

